// Weighted, diacritic-insensitive search across all layers. Every feature gets a
// pre-computed haystack (built once at load); scoring is O(features × tokens).
// A feature matches only if EVERY token hits at least one field.
import type { EquinePoint, Feature, FoodPoint, LayerPoint, SessionPoint, Site } from "../types";
import { stripDiacritics } from "../utils/format";
import { DISH_SEARCH_WORDS, dishLabel, disciplineLabel, kindLabel, mealLabel } from "./layers";

const W = {
  name_exact: 200,
  name_prefix: 80,
  name_token: 40,
  alias_exact: 120,
  alias_prefix: 50,
  alias_token: 25,
  /** A meal ("breakfast") or a dish's search words outrank an alias substring hit. */
  meal: 30,
  kind: 12,
  type: 10,
  tag: 8,
  place: 6,
  description: 2,
};

interface Index {
  names: string[];
  aliases: string[];
  kinds: string; // types / kinds / dishes, space-joined
  /** food only: meals served + dish search words ("breakfast fry …"), space-joined */
  meals: string;
  tags: string;
  place: string; // county, town, region
  desc: string;
}

const INDEX = new WeakMap<Feature, Index>();

export function buildIndex(f: Feature): void {
  const names: string[] = [];
  const aliases: string[] = [];
  let kinds = "";
  let meals = "";
  let tags = "";
  let place = "";
  let desc = "";
  if (f.layer === "history") {
    const s = f.raw as Site;
    names.push(...[s.general_name, s.general_name_en, s.id.replace(/-/g, " ")].filter((x): x is string => !!x).map(stripDiacritics));
    aliases.push(...(s.aliases || []).flatMap((a) => [a.name, a.name_ga, a.name_en]).filter((x): x is string => !!x).map(stripDiacritics));
    kinds = (s.types || []).map((t) => t.replace(/_/g, " ")).join(" ");
    tags = (s.tags || []).map(stripDiacritics).join(" ");
    place = stripDiacritics([s._region?.replace(/-/g, " "), s.county].filter(Boolean).join(" "));
    desc = stripDiacritics([s.description, ...(s.aliases || []).map((a) => a.description)].filter(Boolean).join(" "));
  } else if (f.layer === "sessions") {
    const s = f.raw as SessionPoint;
    names.push(stripDiacritics(s.name));
    place = stripDiacritics([s.town, s.county].filter(Boolean).join(" "));
    kinds = "session trad music pub";
  } else {
    const p = f.raw as LayerPoint & FoodPoint;
    names.push(...[p.name, p.name_ga].filter((x): x is string => !!x).map(stripDiacritics));
    kinds = stripDiacritics([...(p.kinds || []).map(kindLabel), ...(p.kinds || []), ...(p.dishes || []).map(dishLabel), ...(p.dishes || [])].join(" ").replace(/_/g, " "));
    if (f.layer === "food") {
      const words = new Set<string>();
      for (const m of p.meal || []) { words.add(m); for (const w of mealLabel(m).toLowerCase().split(" ")) words.add(w); }
      for (const d of p.dishes || []) for (const w of (DISH_SEARCH_WORDS[d] || "").split(" ")) if (w) words.add(w);
      meals = stripDiacritics([...words].join(" "));
    }
    if (f.layer === "equine") {
      // "racing", "races", "horses", "eventing" must all find a racecourse, and the
      // discipline words are worth searching on their own.
      const eq = f.raw as EquinePoint;
      kinds += " " + stripDiacritics([
        "racing races horse horses equine",
        ...(eq.disciplines || []).map(disciplineLabel),
        ...(eq.disciplines || []),
        ...(eq.feature_meetings || []),
      ].join(" ").replace(/_/g, " "));
    }
    tags = (p.tags || []).map(stripDiacritics).join(" ");
    place = stripDiacritics(p.county || "");
    desc = stripDiacritics([p.summary, p.description, p.locals_note].filter(Boolean).join(" "));
  }
  INDEX.set(f, { names, aliases, kinds, meals, tags, place, desc });
}

export interface Match {
  score: number;
  primary: "name" | "alias" | "kind" | "place" | "description" | null;
}

const RANK: Record<string, number> = { name: 0, alias: 1, kind: 2, place: 3, description: 4 };

export function scoreFeature(f: Feature, tokens: string[]): Match {
  if (!tokens.length) return { score: 0, primary: null };
  const idx = INDEX.get(f);
  if (!idx) return { score: 0, primary: null };
  let total = 0;
  let primary: Match["primary"] = null;
  for (const tok of tokens) {
    let best = 0;
    let field: Match["primary"] = null;
    for (const n of idx.names) {
      if (n === tok) { if (W.name_exact > best) { best = W.name_exact; field = "name"; } }
      else if (n.startsWith(tok)) { if (W.name_prefix > best) { best = W.name_prefix; field = "name"; } }
      else if (n.includes(tok)) { if (W.name_token > best) { best = W.name_token; field = "name"; } }
    }
    for (const a of idx.aliases) {
      if (a === tok) { if (W.alias_exact > best) { best = W.alias_exact; field = "alias"; } }
      else if (a.startsWith(tok)) { if (W.alias_prefix > best) { best = W.alias_prefix; field = "alias"; } }
      else if (a.includes(tok)) { if (W.alias_token > best) { best = W.alias_token; field = "alias"; } }
    }
    if (idx.meals && W.meal > best && idx.meals.split(" ").some((w) => w.startsWith(tok))) { best = W.meal; field = field ?? "kind"; }
    if (idx.kinds.includes(tok) && W.kind > best) { best = W.kind; field = field ?? "kind"; }
    if (idx.tags.includes(tok) && W.tag > best) { best = W.tag; field = field ?? "kind"; }
    if (idx.place.includes(tok) && W.place > best) { best = W.place; field = field ?? "place"; }
    if (idx.desc.includes(tok) && W.description > best) { best = W.description; field = field ?? "description"; }
    if (!best) return { score: 0, primary: null };
    total += best;
    if (!primary || (field && RANK[field] < RANK[primary])) primary = field;
  }
  return { score: total, primary };
}

/** Name/alias-only scoring for the typeahead (predictable suggestions). */
export function typeaheadScore(f: Feature, tokens: string[]): number {
  const idx = INDEX.get(f);
  if (!idx) return 0;
  let total = 0;
  for (const tok of tokens) {
    let best = 0;
    for (const n of idx.names) {
      if (n === tok) best = Math.max(best, W.name_exact);
      else if (n.startsWith(tok)) best = Math.max(best, W.name_prefix);
      else if (n.includes(tok)) best = Math.max(best, W.name_token);
    }
    for (const a of idx.aliases) {
      if (a === tok) best = Math.max(best, W.alias_exact);
      else if (a.startsWith(tok)) best = Math.max(best, W.alias_prefix);
      else if (a.includes(tok)) best = Math.max(best, W.alias_token);
    }
    // Let a town/county name pull sessions and food spots into the typeahead, and a
    // dish or kind ("chowder", "viewpoint", "chipper") pull in the non-history layers.
    if (!best && idx.meals && idx.meals.split(" ").some((w) => w.startsWith(tok))) best = W.meal;
    if (!best && idx.place.split(" ").some((w) => w.startsWith(tok))) best = W.place;
    if (!best && f.layer !== "history" && idx.kinds.split(" ").some((w) => w.startsWith(tok))) best = W.kind;
    if (!best) return 0;
    total += best;
  }
  return total;
}

/** Wrap matching runs in <mark>, diacritic-insensitively. Returns HTML. */
export function highlight(text: string, tokens: string[]): string {
  const esc = (s: string) => s.replace(/[&<>"']/g, (c) => ({ "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;", "'": "&#39;" })[c] as string);
  const norm = stripDiacritics(text);
  if (norm.length !== text.length) return esc(text); // normalization changed length; skip highlighting
  const ranges: [number, number][] = [];
  for (const tok of tokens) {
    let from = 0, i: number;
    while ((i = norm.indexOf(tok, from)) !== -1) { ranges.push([i, i + tok.length]); from = i + tok.length; }
  }
  if (!ranges.length) return esc(text);
  ranges.sort((a, b) => a[0] - b[0]);
  const merged: [number, number][] = [ranges[0]];
  for (let i = 1; i < ranges.length; i++) {
    const last = merged[merged.length - 1];
    if (ranges[i][0] <= last[1]) last[1] = Math.max(last[1], ranges[i][1]);
    else merged.push(ranges[i]);
  }
  let out = "", cur = 0;
  for (const [a, b] of merged) { out += esc(text.slice(cur, a)) + "<mark>" + esc(text.slice(a, b)) + "</mark>"; cur = b; }
  return out + esc(text.slice(cur));
}
